import { useEffect, useState, type FC } from 'react'

import { LoaderWrapper, MessagePopup, NotificationBubble } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { getWhiteLabelForDealerStyle, useDebounce } from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'
import { twJoin, twMerge } from 'tailwind-merge'

import { useAppContext } from '@/App.context'
import Page from '@/components/content/Page'
import { Translation } from '@/components/i18n/Translation'
import subscriptionOrderResources from '@/components/subscription-orders/resources'
import { useSearchParamsByKey, useSortParams } from '@/hooks/searchParams'

import { DEFAULT_ORDER_TYPE } from './consts'
import { getColorByWhiteLabel } from './helpers'
import { queries } from './queries'
import { SubscriptionOrderPortingList } from './SubscriptionOrderPortingList'
import { SubscriptionOrderTerminationList } from './SubscriptionOrderTerminationList'
import { SubscriptionOrderUpgradeList } from './SubscriptionOrderUpgradeList'
import type { CommonOrderKeys, OrderType } from './types'

export const SubscriptionOrders: FC = () => {
  const [, { resetSort }] = useSortParams<CommonOrderKeys>()

  const [querySearch, setQuerySearch] = useSearchParamsByKey('search')
  const [searchFilter, setSearchFilter] = useState(querySearch || '')

  const [queryOrderStatus, setQueryOrderStatus] = useSearchParamsByKey('status')
  const [orderStatusFilter, setOrderStatusFilter] = useState(queryOrderStatus || 'open')

  const [queryOrderType, setQueryOrderType] = useSearchParamsByKey('type')
  const [orderTypeFilter, setOrderTypeFilter] = useState(queryOrderType || DEFAULT_ORDER_TYPE)

  const [queryOrderTerminationType, setQueryOrderTerminationType] = useSearchParamsByKey(
    'terminationType',
    ''
  )

  const debouncedSearchTerm = useDebounce(searchFilter, 500)

  useEffect(() => {
    setSearchFilter(querySearch || '')
    setOrderStatusFilter(queryOrderStatus || 'open')
    setOrderTypeFilter(queryOrderType || DEFAULT_ORDER_TYPE)
  }, [queryOrderStatus, queryOrderType, querySearch, queryOrderTerminationType])

  useEffect(() => {
    setQuerySearch(debouncedSearchTerm)
  }, [debouncedSearchTerm, setQuerySearch])

  const [error, setError] = useState('')

  const { whiteLabel } = useAppContext()

  const { data, isLoading } = useQuery(
    queries.subscriptionOrdersMetaData(),
    subscriptionOrderResources.getOrdersMetaData
  )

  if (!data)
    return (
      <LoaderWrapper isLoading={isLoading} whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)} />
    )
  if (!data) throw Error('Something went wrong when fetching the data. Please refresh the page!')

  function resetFilteringAfterOrderTypeReselection() {
    setQueryOrderStatus('open')
    setOrderStatusFilter('open')
    setQueryOrderTerminationType('')
    resetSort()
  }

  const filterByOrderType = (filter: OrderType) => {
    setQueryOrderType(filter)
    setOrderTypeFilter(filter)
    resetFilteringAfterOrderTypeReselection()
  }

  const themeBtnTextColor = getColorByWhiteLabel(whiteLabel)
  const commonBtnStyle = `shadow-basic m-2 min-w-[10rem] flex-1 basis-[10rem] rounded-full border-b-0 bg-white md:mb-0 md:min-w-0 md:flex-none md:basis-auto md:rounded-b-none md:rounded-t-2xl`

  return (
    <Page>
      <h1>
        <Translation id="Subscription Orders" />
      </h1>

      <div className="m-0 flex flex-wrap md:ml-4 md:flex-nowrap md:overflow-hidden">
        <Button
          variant="secondary"
          onClick={() => filterByOrderType('porting')}
          selected={orderTypeFilter === 'porting'}
          className={twJoin(
            commonBtnStyle,
            themeBtnTextColor.interactive,
            orderTypeFilter === 'porting' && themeBtnTextColor.base,
            'overflow-hidden md:mx-0'
          )}
          textClassName="overflow-hidden"
          whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
        >
          <Translation
            id="TYPE_SUBSCRIPTION_ORDER/PORTING"
            className="overflow-hidden text-ellipsis whitespace-nowrap"
          />
          {data && data.onboardingCount.open > 0 && (
            <NotificationBubble
              isDealer
              variant="secondary"
              selected={orderTypeFilter === 'porting'}
              amount={data.onboardingCount.open}
              className="sticky"
            />
          )}
        </Button>

        <Button
          variant="secondary"
          onClick={() => filterByOrderType('termination')}
          selected={orderTypeFilter === 'termination'}
          className={twMerge(
            commonBtnStyle,
            themeBtnTextColor.interactive,
            orderTypeFilter === 'termination' && themeBtnTextColor.base,
            'overflow-hidden md:mx-0'
          )}
          textClassName="overflow-hidden"
          whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
        >
          <Translation
            id="TYPE_SUBSCRIPTION_ORDER/TERMINATION"
            className="overflow-hidden text-ellipsis whitespace-nowrap"
          />

          {data && data.terminationCount.open > 0 && (
            <NotificationBubble
              isDealer
              variant="secondary"
              selected={orderTypeFilter === 'termination'}
              amount={data.terminationCount.open}
              className="sticky"
            />
          )}
        </Button>

        <Button
          variant="secondary"
          onClick={() => filterByOrderType('upgrade')}
          selected={orderTypeFilter === 'upgrade'}
          className={twJoin(
            commonBtnStyle,
            themeBtnTextColor.interactive,
            orderTypeFilter === 'upgrade' && themeBtnTextColor.base,
            'overflow-hidden md:ml-0'
          )}
          textClassName="overflow-hidden"
          whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
        >
          <Translation
            id="TYPE_SUBSCRIPTION_ORDER/UPGRADE"
            className="overflow-hidden text-ellipsis whitespace-nowrap"
          />

          {data && data.upgradeCount.open > 0 && (
            <NotificationBubble
              isDealer
              variant="secondary"
              selected={orderTypeFilter === 'upgrade'}
              amount={data.upgradeCount.open}
              className="sticky"
            />
          )}
        </Button>
      </div>

      {orderTypeFilter === 'porting' && (
        <SubscriptionOrderPortingList
          statusCounter={data.onboardingCount}
          textSearch={searchFilter}
          status={orderStatusFilter}
          querySearch={querySearch || ''}
          setTextSearch={(text: string) => setSearchFilter(text)}
        />
      )}
      {orderTypeFilter === 'termination' && (
        <SubscriptionOrderTerminationList
          statusCounter={data.terminationCount}
          textSearch={searchFilter}
          querySearch={querySearch || ''}
          status={orderStatusFilter}
          setTextSearch={(text: string) => setSearchFilter(text)}
        />
      )}
      {orderTypeFilter === 'upgrade' && (
        <SubscriptionOrderUpgradeList
          statusCounter={data.upgradeCount}
          status={orderStatusFilter}
          querySearch={querySearch || ''}
          textSearch={searchFilter}
          setTextSearch={(text: string) => setSearchFilter(text)}
        />
      )}

      <MessagePopup
        title={<Translation id="Fetching orders error" />}
        message={
          <>
            <Translation id="We encountered a problem while fetching the orders. Please refresh and try again." />{' '}
            <Translation id="Error" />: {error}
          </>
        }
        isOpen={Boolean(error)}
        onClose={() => setError('')}
        whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
      />
    </Page>
  )
}
